/**
 * smoothScrollTo.ts
 * A lightweight smooth scrolling utility with cubic-bezier support
 * Based on the approach from easy-scroll by Tarun Dugar
 * https://github.com/tarun-dugar/easy-scroll
 */

// Define easing presets
export const easingPresets = {
  linear: (t: number) => t,
  easeInQuad: (t: number) => t * t,
  easeOutQuad: (t: number) => t * (2 - t),
  easeInOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  easeInCubic: (t: number) => t * t * t,
  easeOutCubic: (t: number) => {
    const t1 = t - 1;
    return t1 * t1 * t1 + 1;
  },
  easeInOutCubic: (t: number) =>
    t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
};

// Global state to track if scrolling is in progress
const isScrollingState = {
  value: false,
};

/**
 * Calculate a point on a cubic Bezier curve
 * @param p1x X coordinate of control point 1
 * @param p1y Y coordinate of control point 1
 * @param p2x X coordinate of control point 2
 * @param p2y Y coordinate of control point 2
 * @param t Time parameter (0-1)
 * @returns Point value at t
 */
function cubicBezier(
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
  t: number,
): number {
  // Clamp t to [0, 1]
  const tClamped = Math.max(0, Math.min(1, t));

  // For simple cases where the curve is approximately linear
  if (p1x === p1y && p2x === p2y) {
    return tClamped;
  }

  // Calculate the polynomial coefficients
  const cx = 3.0 * p1x;
  const bx = 3.0 * (p2x - p1x) - cx;
  const ax = 1.0 - cx - bx;

  const cy = 3.0 * p1y;
  const by = 3.0 * (p2y - p1y) - cy;
  const ay = 1.0 - cy - by;

  // Use Newton-Raphson iteration to solve for t given x
  function sampleCurveX(tt: number): number {
    // `ax t^3 + bx t^2 + cx t` expanded
    return ((ax * tt + bx) * tt + cx) * tt;
  }

  function sampleCurveY(tt: number): number {
    return ((ay * tt + by) * tt + cy) * tt;
  }

  function sampleCurveDerivativeX(tt: number): number {
    return (3.0 * ax * tt + 2.0 * bx) * tt + cx;
  }

  // Given an x value, find the parameter value t that gives the same x value
  function solveCurveX(xx: number): number {
    let t2 = xx;
    let derivative;

    // Newton-Raphson method, up to 8 iterations
    for (let i = 0; i < 8; i += 1) {
      // f(t) - x = 0, find t where f(t) = x
      const xSample = sampleCurveX(t2) - xx;
      if (Math.abs(xSample) < 1e-6) {
        return t2;
      }

      derivative = sampleCurveDerivativeX(t2);
      if (Math.abs(derivative) < 1e-6) {
        break;
      }

      t2 -= xSample / derivative;
    }

    // Fall back to bisection method if Newton-Raphson doesn't converge
    let t0 = 0;
    let t1 = 1;
    t2 = xx;

    if (t2 < t0) return t0;
    if (t2 > t1) return t1;

    while (t0 < t1) {
      const xSample = sampleCurveX(t2);
      if (Math.abs(xSample - xx) < 1e-6) {
        return t2;
      }

      if (xx > xSample) {
        t0 = t2;
      } else {
        t1 = t2;
      }

      t2 = (t1 - t0) * 0.5 + t0;
    }

    // Failed to converge, return the last value
    return t2;
  }

  // Solve for t given our input
  const t2 = solveCurveX(tClamped);

  // Return the y value
  return sampleCurveY(t2);
}

type EasingFunction = (t: number) => number;

/**
 * Smooth scroll to a specific position with custom easing
 * @param element DOM element to scroll
 * @param target Target scroll position
 * @param duration Duration of scroll animation in ms
 * @param easing Easing function to use for animation
 */
export function smoothScrollToWithEasing(
  element: HTMLElement,
  target: number,
  duration = 500,
  easing: EasingFunction = easingPresets.easeInOutCubic,
): void {
  // Don't allow multiple scrolls at once
  if (isScrollingState.value) {
    return;
  }

  isScrollingState.value = true;

  // Get current position
  const start = element.scrollTop;
  const distance = target - start;
  const startTime = performance.now();

  // Animation loop
  function scroll(currentTime: number): void {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easingValue = easing(progress);

    // Update scroll position
    element.scrollTop = start + distance * easingValue;

    // Check if we're done
    if (progress < 1) {
      requestAnimationFrame(scroll);
    } else {
      isScrollingState.value = false;
    }
  }

  requestAnimationFrame(scroll);
}

/**
 * Smooth scroll to a specific position with cubic bezier easing
 * @param element DOM element to scroll
 * @param target Target scroll position
 * @param duration Duration of scroll animation in ms
 * @param p1x X coordinate of first bezier control point
 * @param p1y Y coordinate of first bezier control point
 * @param p2x X coordinate of second bezier control point
 * @param p2y Y coordinate of second bezier control point
 */
export function smoothScrollToWithBezier(
  element: HTMLElement,
  target: number,
  duration = 500,
  p1x = 0.45,
  p1y = 0,
  p2x = 0.55,
  p2y = 1,
): void {
  // Create a custom easing function
  const bezierEasing = (t: number): number =>
    cubicBezier(p1x, p1y, p2x, p2y, t);

  smoothScrollToWithEasing(element, target, duration, bezierEasing);
}
